import { User, Therapist } from "../types/user";
import { authenticatedFetch } from "../utils/api";
import { formatDateToYYYYMMDD } from "../utils/date";

export interface TimeSlot {
  time: string;
  iso?: string;
  available: boolean;
}

export interface DateAvailability {
  date: string;
  hasSlots: boolean;
}

export interface Appointment {
  id: string;
  therapistId: string;
  patientId: string;
  date: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  therapist: Therapist;
  patient: User;
  createdAt: string;
  updatedAt: string;
}

export const fetchMonthAvailability = async (
  therapistId: string,
  startDate: Date,
  endDate: Date
): Promise<DateAvailability[]> => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/api/appointments/month-availability?` +
    `therapistId=${therapistId}&` +
    `startDate=${formatDateToYYYYMMDD(startDate)}&` +
    `endDate=${formatDateToYYYYMMDD(endDate)}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch month availability');
  }

  return response.json();
};

export const fetchAvailableSlots = async (
  therapistId: string,
  date: Date
): Promise<TimeSlot[]> => {
  const formattedDate = formatDateToYYYYMMDD(date);
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/api/appointments/available-slots?` +
    `therapistId=${therapistId}&` +
    `date=${formattedDate}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch available slots');
  }

  return response.json();
};

export const bookAppointment = async (
  therapistId: string,
  date: Date,
  time: string
): Promise<Appointment> => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Vous devez être connecté pour prendre rendez-vous');
  }

  const formattedDate = formatDateToYYYYMMDD(date);
  const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/api/appointments`, {
    method: 'POST',
    body: JSON.stringify({
      therapistId,
      date: formattedDate,
      time,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to book appointment');
  }

  return response.json();
};

export const rescheduleAppointment = async (
  appointmentId: string,
  date: Date,
  time: string
): Promise<Appointment> => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Vous devez être connecté pour modifier un rendez-vous');
  }

  const formattedDate = formatDateToYYYYMMDD(date);
  const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/api/appointments/${appointmentId}/reschedule`, {
    method: 'POST',
    body: JSON.stringify({
      date: formattedDate,
      time,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reschedule appointment');
  }

  return response.json();
};